import { LegalLayout } from "../components/legal-layout.js";

const SECTIONS = [
  { id: "who", label: "Who we are" },
  { id: "collect", label: "What we collect" },
  { id: "use", label: "How we use it" },
  { id: "storage", label: "Where it lives" },
  { id: "cookies", label: "Cookies" },
  { id: "sharing", label: "Sharing" },
  { id: "retention", label: "Retention & deletion" },
  { id: "rights", label: "Your rights" },
  { id: "children", label: "Children" },
  { id: "changes", label: "Changes" },
  { id: "contact", label: "Contact" },
] as const;

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="August 21, 2026" sections={SECTIONS}>
      <section id="who" className="docs2__section">
        <h2>Who we are</h2>
        <p>
          saucer.sh is operated by <strong>Routing Run Private Limited</strong>, a company
          incorporated in India (&ldquo;saucer&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). This
          policy explains what personal data we collect when you use saucer.sh, why we collect
          it, and the choices you have.
        </p>
      </section>

      <section id="collect" className="docs2__section">
        <h2>What we collect</h2>
        <p>Signing in and using the product gives us a small, specific set of data:</p>
        <div className="cmdrows">
          <div className="cmdrows__row">
            <code className="mono">Account</code>
            <span>GitHub username, email and avatar returned by GitHub OAuth on sign-in.</span>
          </div>
          <div className="cmdrows__row">
            <code className="mono">Content</code>
            <span>Files you upload, their filenames, and AI-generated titles/descriptions/tags used for search.</span>
          </div>
          <div className="cmdrows__row">
            <code className="mono">Credentials</code>
            <span>Session and CLI tokens and file passwords — stored only as salted hashes, never in plaintext.</span>
          </div>
          <div className="cmdrows__row">
            <code className="mono">Logs</code>
            <span>Request metadata (IP, user agent, timestamp, path) kept for abuse prevention and debugging.</span>
          </div>
        </div>
      </section>

      <section id="use" className="docs2__section">
        <h2>How we use it</h2>
        <p>
          We use this data to operate the service: authenticate you, serve and render your
          files, power search and the assistant over your own account, prevent abuse, and
          keep the service reliable. We do not sell personal data, and we do not use your
          uploaded file contents to train models outside the per-account AI features described
          in the <a href="/docs">docs</a>.
        </p>
      </section>

      <section id="storage" className="docs2__section">
        <h2>Where it lives</h2>
        <p>
          saucer runs on Cloudflare&rsquo;s network. Files are stored in Cloudflare R2, metadata
          in Cloudflare D1, and short-lived session state in Cloudflare KV and Durable Objects.
          Data may be processed in whichever region Cloudflare routes the request to; we do not
          separately mirror it to other infrastructure.
        </p>
      </section>

      <section id="cookies" className="docs2__section">
        <h2>Cookies</h2>
        <p>
          We set one first-party, HTTP-only session cookie to keep you signed in. It carries no
          third-party trackers and no advertising identifiers, and it is deleted when you sign
          out.
        </p>
      </section>

      <section id="sharing" className="docs2__section">
        <h2>Sharing</h2>
        <p>
          We share data only where the product requires it (Cloudflare, as our infrastructure
          provider) or where the law requires it — for example, a valid takedown or law
          enforcement request handled under our <a href="/dmca">DMCA policy</a>. We do not sell
          or rent personal data to third parties.
        </p>
      </section>

      <section id="retention" className="docs2__section">
        <h2>Retention &amp; deletion</h2>
        <p>
          Deleting a file removes its link immediately; the underlying storage object and its
          search index are swept in the background shortly after. Closing your account removes
          your files the same way. Request logs are retained for a limited period for security
          purposes and then discarded.
        </p>
      </section>

      <section id="rights" className="docs2__section">
        <h2>Your rights</h2>
        <p>
          Under India&rsquo;s Digital Personal Data Protection Act, 2023, and comparable laws
          elsewhere, you can ask us to access, correct, or erase your personal data, or to tell
          you what we hold. Email{" "}
          <a href="mailto:email@saucer.sh">email@saucer.sh</a> and we will respond within a
          reasonable time.
        </p>
      </section>

      <section id="children" className="docs2__section">
        <h2>Children</h2>
        <p>
          saucer.sh is not directed at children and we do not knowingly collect data from
          anyone under 18. Signing up requires a GitHub account, which carries its own
          minimum age, but if you believe a minor&rsquo;s data has reached us, contact us
          and we will remove it.
        </p>
      </section>

      <section id="changes" className="docs2__section">
        <h2>Changes</h2>
        <p>
          We may update this policy as the product changes. Material changes will be reflected
          by updating the date at the top of this page.
        </p>
      </section>

      <section id="contact" className="docs2__section">
        <h2>Contact</h2>
        <p>
          Questions about this policy: <a href="mailto:email@saucer.sh">email@saucer.sh</a>.
          Copyright/takedown requests go to{" "}
          <a href="mailto:dmca@saucer.sh">dmca@saucer.sh</a> under our{" "}
          <a href="/dmca">DMCA policy</a>.
        </p>
      </section>
    </LegalLayout>
  );
}
